import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from '../../utils/analytics';
import { getContentType } from '../../utils/contentType';
import { useMusicState } from '../storeHooks/useMusicState';

interface UseAudioTimeTrackingProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  isPlaying: boolean;
  setProgress: (progress: number) => void;
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;
}

export const useAudioTimeTracking = ({
  audioRef,
  isPlaying,
  setProgress,
  setCurrentTime,
  setDuration,
}: UseAudioTimeTrackingProps) => {
  const location = useLocation();
  const { activePlaylist, shuffleEnabled } = useMusicState();

  // Track which tracks have had content_completed logged
  const contentCompletedLoggedRef = useRef<Set<string>>(new Set());
  // First good duration for this src; may move up once if we learn a longer real length.
  const durationLockRef = useRef<number | null>(null);
  // After one upward correction we stop (avoids EOF creep 40:02 → 40:04 → …).
  const durationRefinementConsumedRef = useRef(false);
  const lastSrcRef = useRef<string>('');

  const DURATION_REFINE_EPSILON_SEC = 0.25;

  // Attach timeupdate listener
  useEffect(() => {
    const currentAudio = audioRef.current;
    if (!currentAudio) return;
    const currentTrackDuration =
      activePlaylist?.album?.tracks?.[activePlaylist.currentIndex]?.duration;

    const apiDuration = Number(currentTrackDuration);
    const hasApiDuration = Number.isFinite(apiDuration) && apiDuration > 0;

    // duration can lag behind what's actually buffered; seekable end is often ahead on long assets.
    const getReportedTimelineEnd = (audio: HTMLAudioElement): number | null => {
      let best = 0;
      const d = audio.duration;
      if (isFinite(d) && d > 0) best = Math.max(best, d);
      try {
        const ranges = audio.seekable;
        if (ranges.length > 0) {
          const end = ranges.end(ranges.length - 1);
          if (isFinite(end) && end > 0) best = Math.max(best, end);
        }
      } catch {
        // Rare: seekable access issues on detached / odd states
      }
      return best > 0 ? best : null;
    };

    // Show the best total length as early as possible: if both API and <audio> report a
    // length, use the larger so we don't show 40:00 while the file is actually 40:02.
    const computeDisplayDuration = (metadataDuration: number) => {
      if (!isFinite(metadataDuration) || metadataDuration <= 0) {
        return hasApiDuration ? apiDuration : 0;
      }
      if (!hasApiDuration) {
        return metadataDuration;
      }
      return Math.max(apiDuration, metadataDuration);
    };

    const ensureDurationLock = (audio: HTMLAudioElement) => {
      if (durationLockRef.current !== null) return;
      const end = getReportedTimelineEnd(audio);
      if (end === null) return;
      durationLockRef.current = computeDisplayDuration(end);
    };

    // Single allowed bump when the engine reports a longer length (e.g. after full buffer parse).
    const tryRefineDurationOnce = (audio: HTMLAudioElement) => {
      if (durationRefinementConsumedRef.current) return;
      const end = getReportedTimelineEnd(audio);
      if (end === null) return;
      ensureDurationLock(audio);
      const currentLock = durationLockRef.current;
      if (currentLock === null) return;
      const candidate = computeDisplayDuration(end);
      if (candidate > currentLock + DURATION_REFINE_EPSILON_SEC) {
        durationLockRef.current = candidate;
        durationRefinementConsumedRef.current = true;
        setDuration(candidate);
      }
    };

    // Elapsed and the right-hand total stay on the lock so the bar doesn’t “grow” at the end.
    // If the file runs slightly longer, progress can sit at 100% until `ended` (audio still plays).
    const clampDisplayTime = (rawSeconds: number, displayTotal: number) => {
      if (!(displayTotal > 0) || !isFinite(rawSeconds)) return rawSeconds;
      return Math.min(rawSeconds, displayTotal);
    };

    const handleTimeUpdate = () => {
      const audio = audioRef.current;
      const currentSrc = audio?.currentSrc || audio?.src || '';
      if (currentSrc && currentSrc !== lastSrcRef.current) {
        lastSrcRef.current = currentSrc;
        durationLockRef.current = null;
        durationRefinementConsumedRef.current = false;
      }
      if (
        audio &&
        isFinite(audio.duration) &&
        audio.duration > 0 &&
        isFinite(audio.currentTime)
      ) {
        ensureDurationLock(audio);
        // durationchange/canplaythrough often never fire when the engine revises length;
        // polling here (until our one bump is used) picks that up reliably.
        if (!durationRefinementConsumedRef.current) {
          tryRefineDurationOnce(audio);
        }

        const metaDuration = audio.duration;
        const stableDuration = durationLockRef.current || metaDuration;
        const displayTime = clampDisplayTime(audio.currentTime, stableDuration);
        const newProgress =
          stableDuration > 0 ? (displayTime / stableDuration) * 100 : 0;

        // Ensure all values are finite before setting state
        if (
          isFinite(newProgress) &&
          isFinite(displayTime) &&
          isFinite(stableDuration)
        ) {
          setProgress(Math.max(0, Math.min(100, newProgress)));
          setCurrentTime(displayTime);
          setDuration(stableDuration);

          // Track content_completed when reaching 90% completion
          if (newProgress >= 90 && activePlaylist) {
            const album = activePlaylist.album;
            const track = album.tracks[activePlaylist.currentIndex];
            const trackId = track?.id;

            if (trackId && !contentCompletedLoggedRef.current.has(trackId)) {
              contentCompletedLoggedRef.current.add(trackId);

              try {
                analytics.logEvent('content_completed', {
                  content_type: getContentType(album.category),
                  content_id: trackId,
                  duration_played_seconds: Math.round(displayTime),
                  source_page: `${location.pathname}${location.search || ''}`,
                });
              } catch (error) {
                console.error('Error tracking content_completed:', error);
              }
            }
          }
        } else {
          console.warn('Non-finite values detected in time update:', {
            progress: newProgress,
            currentTime: displayTime,
            duration: stableDuration,
          });
        }
      }
    };

    const handleLoadedMetadata = () => {
      const audio = audioRef.current;
      if (audio) {
        const currentSrc = audio.currentSrc || audio.src || '';
        if (currentSrc && currentSrc !== lastSrcRef.current) {
          lastSrcRef.current = currentSrc;
          durationLockRef.current = null;
          durationRefinementConsumedRef.current = false;
        }

        // Validate duration and currentTime are finite before setting
        if (isFinite(audio.duration) && audio.duration > 0) {
          ensureDurationLock(audio);
          tryRefineDurationOnce(audio);
          const stableDuration = durationLockRef.current ?? audio.duration;
          setDuration(stableDuration);

          if (isFinite(audio.currentTime)) {
            setCurrentTime(
              clampDisplayTime(audio.currentTime, stableDuration),
            );
          } else {
            console.warn(
              'Invalid currentTime, setting to 0:',
              audio.currentTime,
            );
            setCurrentTime(0);
            // Try to reset audio currentTime safely
            try {
              audio.currentTime = 0;
            } catch (error) {
              console.warn('Could not reset audio currentTime:', error);
            }
          }
        } else {
          console.warn('Invalid audio duration:', audio.duration);
          // Set safe default values
          setDuration(0);
          setCurrentTime(0);
        }
      }
      handleTimeUpdate(); // Update progress when metadata loads
    };

    const handleCanPlay = () => {
      handleTimeUpdate(); // Update progress when audio can play
    };

    const handleCanPlayThrough = () => {
      const audio = audioRef.current;
      if (!audio || getReportedTimelineEnd(audio) === null) return;
      ensureDurationLock(audio);
      tryRefineDurationOnce(audio);
      if (durationLockRef.current !== null) {
        setDuration(durationLockRef.current);
      }
      handleTimeUpdate();
    };

    const handleDurationChange = () => {
      const audio = audioRef.current;
      if (!audio) return;
      tryRefineDurationOnce(audio);
      handleTimeUpdate();
    };

    // Same idea as timeupdate: more data buffered can expose a truer duration without a durationchange.
    const handleProgress = () => {
      const audio = audioRef.current;
      if (!audio) return;
      if (!durationRefinementConsumedRef.current) {
        tryRefineDurationOnce(audio);
      }
    };

    const handleLoadStart = (): void => {
      durationLockRef.current = null;
      durationRefinementConsumedRef.current = false;
    };

    const handleSeeked = () => {
      console.log('Audio seeked event fired:', {
        currentTime: currentAudio.currentTime,
        duration: currentAudio.duration,
      });
      handleTimeUpdate(); // Update progress after seek completes
    };

    const handleSeeking = () => {
      console.log('Audio seeking event fired:', {
        currentTime: currentAudio.currentTime,
        duration: currentAudio.duration,
      });
    };

    // Add multiple event listeners to ensure we catch progress updates
    currentAudio.addEventListener('timeupdate', handleTimeUpdate);
    currentAudio.addEventListener('loadedmetadata', handleLoadedMetadata);
    currentAudio.addEventListener('canplay', handleCanPlay);
    currentAudio.addEventListener('canplaythrough', handleCanPlayThrough);
    currentAudio.addEventListener('durationchange', handleDurationChange);
    currentAudio.addEventListener('progress', handleProgress);
    currentAudio.addEventListener('loadstart', handleLoadStart);
    currentAudio.addEventListener('seeked', handleSeeked);
    currentAudio.addEventListener('seeking', handleSeeking);

    return () => {
      currentAudio.removeEventListener('timeupdate', handleTimeUpdate);
      currentAudio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      currentAudio.removeEventListener('canplay', handleCanPlay);
      currentAudio.removeEventListener('canplaythrough', handleCanPlayThrough);
      currentAudio.removeEventListener('durationchange', handleDurationChange);
      currentAudio.removeEventListener('progress', handleProgress);
      currentAudio.removeEventListener('loadstart', handleLoadStart);
      currentAudio.removeEventListener('seeked', handleSeeked);
      currentAudio.removeEventListener('seeking', handleSeeking);
    };
  }, [
    activePlaylist,
    activePlaylist?.currentIndex,
    isPlaying,
    setProgress,
    setCurrentTime,
    setDuration,
    audioRef,
    shuffleEnabled,
    location.pathname,
    location.search,
  ]);
};
